import { Route, Switch, useLocation } from "wouter";
import { Suspense, lazy, useEffect } from 'react';
import { SignedIn, SignedOut, RedirectToSignIn, useClerk, useAuth } from "@clerk/clerk-react";
import Toaster from "./components/ui/toaster";
import { LoadingSpinner } from "./components/LoadingSpinner";
import FloatingFeedbackButton from "./components/FloatingFeedbackButton";
import ClerkLoadingFallback from "./components/ClerkLoadingFallback";
import SiteFooter from "./components/SiteFooter";

// Lazy load all pages with error handling
const lazyLoad = (importFn: () => Promise<any>) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error: unknown) {
      console.error('Error loading module:', error);
      // Return a simple fallback component
      return { default: () => <div className="p-8 text-center">Failed to load component. Please refresh the page.</div> };
    }
  });
};

const LandingV2 = lazyLoad(() => import("./pages/LandingV2"));
const LandingPage = lazyLoad(() => import("./pages/LandingPage"));
const UILab = lazyLoad(() => import("./pages/UILab"));
const SignInPage = lazyLoad(() => import("./pages/SignInPage"));
const SignUpPage = lazyLoad(() => import("./pages/SignupPage"));
const SSOCallbackPage = lazyLoad(() => import("./pages/SSOCallbackPage"));
const OnboardingPage = lazyLoad(() => import("./pages/OnboardingPage"));
const DashboardPage = lazyLoad(() => import("./pages/DashboardPage"));
const PaymentsPage = lazyLoad(() => import("./pages/PaymentsPage"));
const AdminPage = lazyLoad(() => import("./pages/AdminPage"));
const TermsPage = lazyLoad(() => import("./pages/TermsPage"));
const PrivacyPage = lazyLoad(() => import("./pages/PrivacyPage"));
const RefundPolicyPage = lazyLoad(() => import("./pages/RefundPolicyPage"));
const FeedbackPage = lazyLoad(() => import("./pages/FeedbackPage"));
const CoachingPage = lazyLoad(() => import("./pages/CoachingPage"));
const CoachingSessionPage = lazyLoad(() => import("./pages/CoachingSessionPage"));
const WishlistPage = lazyLoad(() => import("./pages/WishlistPage"));
const InsightsPage = lazyLoad(() => import("./pages/InsightsPage"));
const InsightsHubPage = lazyLoad(() => import("./pages/InsightsHubPage"));
const InsightsPageV2 = lazyLoad(() => import("./pages/InsightsPageV2"));
const SettingsPage = lazyLoad(() => import("./pages/SettingsPage"));
const TakeawaysArchiveView = lazyLoad(() => import("./pages/TakeawaysArchiveView"));
const MoMDebugPage = lazyLoad(() => import("./pages/MoMDebugPage"));
const GiftPurchasePage = lazyLoad(() => import("./pages/GiftPurchasePage"));
const GiftRedemptionPage = lazyLoad(() => import("./pages/GiftRedemptionPage"));

import ErrorBoundary from './components/ErrorBoundary';
import { useCurrentUserProfile } from './hooks/useCurrentUserProfile';
import { isOnboardingComplete } from './lib/onboardingStatus';
import HomePage from "./pages/HomePage";
import { redeemCoupon } from "./lib/api";

const FullPageLoader = () => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50">
    <LoadingSpinner />
  </div>
);

const FullPageError = ({ onRetry }: { onRetry: () => void }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
    <div className="max-w-sm space-y-4 text-center">
      <p className="text-base font-display font-semibold text-red-600">We hit a snag loading your account.</p>
      <p className="text-sm text-slate-600">Please try again. If the issue persists, refresh the page.</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        Try again
      </button>
    </div>
  </div>
);

const OnboardingRoute = () => {
  const [location, setLocation] = useLocation();
  const { isLoading, isError, data, error } = useCurrentUserProfile();
  const onboardingComplete = data ? isOnboardingComplete(data) : false;

  useEffect(() => {
    // If we finished loading and onboarding is complete, go to dashboard
    if (!isLoading && data && onboardingComplete && location.startsWith('/onboarding')) {
      setLocation('/dashboard');
    }
  }, [isLoading, data, onboardingComplete, location, setLocation]);

  if (isLoading) {
    return <FullPageLoader />;
  }

  // If there's an error but it's not a 404, show the error
  if (isError && (error as any)?.status !== 404) {
    return (
      <div className="fixed left-1/2 top-4 z-50 w-full max-w-md -translate-x-1/2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow">
        <p className="font-semibold">Error loading account profile.</p>
        <p className="mt-1 text-red-600/80">Try refreshing the page.</p>
      </div>
    );
  }

  return <OnboardingPage />;
};

const DashboardRoute = () => {
  const [location, setLocation] = useLocation();
  const { isLoading, isError, data, error, refetch } = useCurrentUserProfile();
  const onboardingComplete = data ? isOnboardingComplete(data) : false;
  const { getToken, isSignedIn } = useAuth();

  // Auto-redeem pending coupon after auth (feature-gated)
  useEffect(() => {
    (async () => {
      try {
        const FEATURE_COUPONS = (import.meta as any).env?.VITE_FEATURE_COUPONS === 'true';
        if (!FEATURE_COUPONS) return;
        if (isLoading) return;
        if (!isSignedIn) return;
        const raw = typeof window !== 'undefined' ? localStorage.getItem('hipo_coupon_pending') : null;
        if (!raw) return;
        const pending = JSON.parse(raw || 'null');
        const code = pending?.code;
        if (!code) return;
        const token = await getToken();
        if (!token) return;
        const resp = await redeemCoupon(code, token);
        if (resp?.success) {
          console.log('Coupon redeemed:', resp);
        } else {
          console.warn('Coupon redeem failed:', resp?.reason);
        }
        localStorage.removeItem('hipo_coupon_pending');
      } catch (e) {
        console.warn('Auto-redeem error:', e);
      }
    })();
  }, [isLoading, isSignedIn, getToken]);

  useEffect(() => {
    // If the profile fetch returned null (getCurrentUserProfile returns null on 404), redirect to onboarding
    if (!isLoading && !isError && data === null && location !== '/onboarding/step-1') {
      console.log('User record not found (null data), redirecting to onboarding');
      setLocation('/onboarding/step-1');
      return;
    }

    // If there's an error and it's 404 (just in case it throws), redirect to onboarding
    if (!isLoading && isError && (error as any)?.status === 404 && location !== '/onboarding/step-1') {
      console.log('User record not found (404 error), redirecting to onboarding');
      setLocation('/onboarding/step-1');
      return;
    }

    // If profile exists but onboarding is incomplete, redirect to onboarding
    if (!isLoading && data && !onboardingComplete && location !== '/onboarding/step-1') {
      console.log('Incomplete profile detected, redirecting to onboarding');
      setLocation('/onboarding/step-1');
    }
  }, [isLoading, isError, data, error, onboardingComplete, location, setLocation]);

  if (isLoading) {
    return <FullPageLoader />;
  }

  if (isError && (error as any)?.status !== 404) {
    return <FullPageError onRetry={() => refetch()} />
  }

  // If we have data and onboarding is complete, show dashboard
  if (data && onboardingComplete) {
    return <HomePage />;
  }

  // Fallback while redirecting or in transition
  return <FullPageLoader />;
};

function App() {
  const { loaded } = useClerk();

  // Show loading fallback while Clerk is initializing
  if (!loaded) {
    return <ClerkLoadingFallback />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<FullPageLoader />}>
        <Switch>
          <Route path="/" component={LandingV2} />
          <Route path="/v1" component={LandingPage} />
          <Route path="/v2" component={LandingV2} />
          <Route path="/ui-lab" component={UILab} />
          {/* SSO Callbacks - Specific routes first */}
          <Route path="/signup/sso-callback" component={SSOCallbackPage} />
          <Route path="/sign-up/sso-callback" component={SSOCallbackPage} />
          <Route path="/sign-in/sso-callback" component={SSOCallbackPage} />
          <Route path="/login/sso-callback" component={SSOCallbackPage} />

          <Route path="/sign-in" component={SignInPage} />
          <Route path="/sign-in/:rest*" component={SignInPage} />
          <Route path="/login" component={SignInPage} />
          <Route path="/login/:rest*" component={SignInPage} />

          {/* Signup Flows */}
          {/* 1. Explicit sub-routes */}
          <Route path="/signup/verify-email-address" component={SignUpPage} />
          <Route path="/sign-up/verify-email-address" component={SignUpPage} />

          {/* 2. Base routes */}
          <Route path="/sign-up" component={SignUpPage} />
          <Route path="/signup" component={SignUpPage} />

          {/* 3. Wildcards for internal sub-routing */}
          <Route path="/sign-up/:rest*" component={SignUpPage} />
          <Route path="/signup/:rest*" component={SignUpPage} />
          <Route path="/login/sso-callback" component={SSOCallbackPage} />
          <Route path="/terms" component={TermsPage} />
          <Route path="/privacy" component={PrivacyPage} />
          <Route path="/refund-policy" component={RefundPolicyPage} />
          <Route path="/onboarding" component={() => {
            const [, setLocation] = useLocation();
            useEffect(() => { setLocation('/onboarding/step-1'); }, [setLocation]);
            return <FullPageLoader />;
          }} />
          <Route path="/onboarding/:step">
            <SignedIn>
              <OnboardingRoute />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/dashboard">
            <SignedIn>
              <DashboardRoute />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/coaching">
            <SignedIn>
              <CoachingPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/coaching/session">
            <SignedIn>
              <CoachingSessionPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/wishlist">
            <SignedIn>
              <WishlistPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/insights-hub">
            <SignedIn>
              <InsightsHubPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/insights">
            <SignedIn>
              {(import.meta as any).env?.VITE_FEATURE_INSIGHTS_V2 === 'true' ? <InsightsPageV2 /> : <InsightsPage />}
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/archive">
            <SignedIn>
              <TakeawaysArchiveView />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/debug-mom">
            <SignedIn>
              <MoMDebugPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/settings">
            <SignedIn>
              <SettingsPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/feedback">
            <SignedIn>
              <FeedbackPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/payments">
            <SignedIn>
              <PaymentsPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/admin">
            <SignedIn>
              <AdminPage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/gift">
            <SignedIn>
              <GiftPurchasePage />
            </SignedIn>
            <SignedOut>
              <RedirectToSignIn />
            </SignedOut>
          </Route>
          <Route path="/gift-redeem">
            <GiftRedemptionPage />
          </Route>
          <Route>404: Not Found!</Route>
        </Switch>
      </Suspense>
      <SiteFooter />
      <FloatingFeedbackButton />
      <Toaster />
    </ErrorBoundary>
  );
}

export default App;
